import { useState, useEffect } from 'react';
import { useAuth } from "../../../../contexts/AuthContext";
import { db } from "../../../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import getServerTimestamp from '../../../../functions/endpoints/GetServerTimestamp';
import { useNavigate, useParams } from "react-router";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorDisplay from '../../../../components/ErrorDisplay';
import Backdrop from "@mui/material/Backdrop";
import * as theme from '../../../../theme';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import TruCard from '../../../../components/TruCard';
import AddableList from '../../../../components/AddableList';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

export default function EditSupplementReport() {
  const params = useParams();
  const clientId = params.clientId;
  const scanId = params.scanId;
  const reportId = params.reportId;
  const isOneOff = !!reportId;
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    followUp: '',
    topItems: [],
    imprintRemedy: { drops: '', am: false, pm: false, duration: '' },
    homeopathics: [],
    asea: {
      summary: false,
      liquid: { enabled: false, first: '', second: '' },
      gel: { enabled: false, first: '', second: '' }
    },
    herbs: {
      groups: [],
      specialNotes: []
    },
    recommendations: {
      therapies: [],
      recommendations: []
    },
    keyNotes: {
      priorities: [],
      notes: []
    },
    itemsToOrder: [],
    itemsToShip: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Collapsible sections state - first section starts expanded, rest collapsed to reduce scrolling on this long form
  const [expandedSections, setExpandedSections] = useState({
    followUp: true,
    topItems: false,
    imprintRemedy: false,
    homeopathics: false,
    asea: false,
    herbs: false,
    recommendations: false,
    keyNotes: false,
    itemsToOrder: false,
    itemsToShip: false,
  });

  const [expandedGroups, setExpandedGroups] = useState([]);

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleGroup = (gIndex) => {
    setExpandedGroups((prev) => {
      const newExpanded = [...prev];
      if (gIndex < newExpanded.length) {
        newExpanded[gIndex] = !newExpanded[gIndex];
      }
      return newExpanded;
    });
  };

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    const getData = async () => {
      if (isOneOff && !reportId) {
        setError('Missing report ID for one-off edit.');
        return;
      }
      if (!isOneOff && (!clientId || !scanId)) {
        setError('Missing client or scan ID for standard edit.');
        return;
      }

      try {
        let docRef;
        if (isOneOff) {
          docRef = doc(db, "oneOffReports", reportId);
        } else {
          docRef = doc(db, "clients", clientId, "scans", scanId);
        }
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const reportData = data.reportData?.supplementSchedule || {};
          if (typeof reportData !== 'object' || reportData === null) {
            setError("This scan uses an older format and cannot be edited here.");
            navigate(isOneOff ? '/admin/reports' : `/clients/${clientId}/scans/${scanId}`);
            return;
          }

          const imprint = reportData.imprintRemedy || {};
          const imprintRemedy = {
            drops: typeof imprint.drops === 'number' ? imprint.drops.toString() : (imprint.drops || ''),
            am: !!imprint.am,
            pm: !!imprint.pm,
            duration: typeof imprint.duration === 'number' ? imprint.duration.toString() : (imprint.duration || ''),
          };

          setFormData({
            followUp: reportData.followUp || '',
            topItems: reportData.topItems || [],
            imprintRemedy,
            homeopathics: (reportData.homeopathics || []).map(h => ({
              item: h.item || '',
              dose: h.dose || '',
              am: !!h.am,
              pm: !!h.pm,
              duration: typeof h.duration === 'number' ? h.duration.toString() : (h.duration || '')
            })),
            asea: {
              summary: !!reportData.asea?.summary,
              liquid: reportData.asea?.liquid || { enabled: false, first: '', second: '' },
              gel: reportData.asea?.gel || { enabled: false, first: '', second: '' }
            },
            herbs: {
              groups: (reportData.herbs?.groups || []).map(g => ({
                heading: g.heading || '',
                showHeading: g.showHeading !== undefined ? !!g.showHeading : true,
                items: (g.items || []).map(item => ({
                  item: item.item || '',
                  dose: item.dose || '',
                  am: !!item.am,
                  midDay: !!item.midDay,
                  pm: !!item.pm,
                  bedtime: !!item.bedtime,
                  duration: item.duration || '',
                  specialNote: item.specialNote || ''
                }))
              })),
              specialNotes: reportData.herbs?.specialNotes || []
            },
            recommendations: reportData.recommendations || { therapies: [], recommendations: [] },
            keyNotes: reportData.keyNotes || { priorities: [], notes: [] },
            itemsToOrder: reportData.itemsToOrder || [],
            itemsToShip: reportData.itemsToShip || []
          });

          // Initialize group expansion state (first group expanded, rest collapsed)
          const loadedGroups = reportData.herbs?.groups || [];
          setExpandedGroups(loadedGroups.map(() => false));
        } else {
          setError("Data not found.");
        }
      } catch (err) {
        setError("Failed to fetch: " + err.message);
      }
    };
    getData();
  }, [clientId, scanId, reportId, isOneOff, navigate]);

  // Follow Up
  const handleFollowUpChange = (e) => {
    setFormData({
      ...formData,
      followUp: e.target.value
    });
  };

  // Top-level array helpers (topItems, itemsToOrder, itemsToShip)
  const addTopLevelItem = (field) => (value) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], value.trim()]
    });
  };
  const removeTopLevelItem = (field) => (index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };
  const updateTopLevelItem = (field) => (index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  // Nested array helpers (for recommendations.therapies, recommendations.recommendations, keyNotes.priorities, keyNotes.notes)
  const addNestedItem = (parentField, childField) => (value) => {
    setFormData({
      ...formData,
      [parentField]: {
        ...formData[parentField],
        [childField]: [...formData[parentField][childField], value.trim()]
      }
    });
  };
  const removeNestedItem = (parentField, childField) => (index) => {
    const updated = formData[parentField][childField].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [parentField]: {
        ...formData[parentField],
        [childField]: updated
      }
    });
  };
  const updateNestedItem = (parentField, childField) => (index, value) => {
    const updated = [...formData[parentField][childField]];
    updated[index] = value;
    setFormData({
      ...formData,
      [parentField]: {
        ...formData[parentField],
        [childField]: updated
      }
    });
  };

  // Imprint Remedy
  const handleImprintChange = (key) => (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      imprintRemedy: { ...formData.imprintRemedy, [key]: value }
    });
  };
  const handleImprintCheckbox = (key) => (e) => {
    setFormData({
      ...formData,
      imprintRemedy: { ...formData.imprintRemedy, [key]: e.target.checked }
    });
  };

  // Homeopathics
  const addHomeopathic = () => {
    setFormData({
      ...formData,
      homeopathics: [
        ...formData.homeopathics,
        { item: '', dose: '', am: false, pm: false, duration: '' }
      ]
    });
  };
  const removeHomeopathic = (index) => {
    const updated = formData.homeopathics.filter((_, i) => i !== index);
    setFormData({ ...formData, homeopathics: updated });
  };
  const updateHomeopathic = (index, key, value) => {
    const updated = [...formData.homeopathics];
    updated[index] = { ...updated[index], [key]: value };
    setFormData({ ...formData, homeopathics: updated });
  };

  // Asea
  const handleAseaCheckbox = (field) => (e) => {
    setFormData({
      ...formData,
      asea: { ...formData.asea, [field]: e.target.checked }
    });
  };
  const handleAseaSubChange = (sub, field) => (e) => {
    setFormData({
      ...formData,
      asea: {
        ...formData.asea,
        [sub]: { ...formData.asea[sub], [field]: e.target.value }
      }
    });
  };
  const handleAseaSubCheckbox = (sub, field) => (e) => {
    setFormData({
      ...formData,
      asea: {
        ...formData.asea,
        [sub]: { ...formData.asea[sub], [field]: e.target.checked }
      }
    });
  };

  // Herbs / Supplements - Group management
  const addHerbGroup = () => {
    setFormData({
      ...formData,
      herbs: {
        ...formData.herbs,
        groups: [
          ...formData.herbs.groups,
          { heading: '', showHeading: true, items: [] }
        ]
      }
    });
    setExpandedGroups((prev) => [...prev, true]); // expand newly added group
  };

  const removeHerbGroup = (gIndex) => {
    const updatedGroups = formData.herbs.groups.filter((_, i) => i !== gIndex);
    setFormData({
      ...formData,
      herbs: { ...formData.herbs, groups: updatedGroups }
    });
    setExpandedGroups((prev) => prev.filter((_, i) => i !== gIndex));
  };

  const updateHerbGroup = (gIndex, key, value) => {
    const updatedGroups = [...formData.herbs.groups];
    updatedGroups[gIndex] = { ...updatedGroups[gIndex], [key]: value };
    setFormData({
      ...formData,
      herbs: { ...formData.herbs, groups: updatedGroups }
    });
  };

  const addHerbItem = (gIndex) => {
    const updatedGroups = [...formData.herbs.groups];
    updatedGroups[gIndex] = {
      ...updatedGroups[gIndex],
      items: [
        ...(updatedGroups[gIndex].items || []),
        { item: '', dose: '', am: false, midDay: false, pm: false, bedtime: false, duration: '', specialNote: '' }
      ]
    };
    setFormData({
      ...formData,
      herbs: { ...formData.herbs, groups: updatedGroups }
    });
  };

  const removeHerbItem = (gIndex, hIndex) => {
    const updatedGroups = [...formData.herbs.groups];
    updatedGroups[gIndex].items = updatedGroups[gIndex].items.filter((_, i) => i !== hIndex);
    setFormData({
      ...formData,
      herbs: { ...formData.herbs, groups: updatedGroups }
    });
  };

  const updateHerbItem = (gIndex, hIndex, key, value) => {
    const updatedGroups = [...formData.herbs.groups];
    const updatedItems = [...(updatedGroups[gIndex].items || [])];
    updatedItems[hIndex] = { ...updatedItems[hIndex], [key]: value };
    updatedGroups[gIndex].items = updatedItems;
    setFormData({
      ...formData,
      herbs: { ...formData.herbs, groups: updatedGroups }
    });
  };

  // Special Notes (inside herbs)
  const addSpecialNote = (value) => {
    setFormData({
      ...formData,
      herbs: {
        ...formData.herbs,
        specialNotes: [...formData.herbs.specialNotes, value.trim()]
      }
    });
  };
  const removeSpecialNote = (index) => {
    const updated = formData.herbs.specialNotes.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      herbs: { ...formData.herbs, specialNotes: updated }
    });
  };
  const updateSpecialNote = (index, value) => {
    const updated = [...formData.herbs.specialNotes];
    updated[index] = value;
    setFormData({
      ...formData,
      herbs: { ...formData.herbs, specialNotes: updated }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const timestamp = await getServerTimestamp();
      const updatedData = { ...formData };

      let docRef;
      if (isOneOff) {
        docRef = doc(db, "oneOffReports", reportId);
      } else {
        docRef = doc(db, "clients", clientId, "scans", scanId);
      }

      await updateDoc(docRef, {
        "reportData.supplementSchedule": updatedData,
        dateModified: timestamp,
      });

      navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`);
    } catch (err) {
      setError("Failed to update supplement report: " + err.message);
    }

    setLoading(false);
  };

  return (
    <div style={theme.Container.basicWide}>
      <div style={theme.Heading}>EDIT SUPPLEMENT REPORT</div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          {/* Follow Up */}
          <TruCard 
            title={'Follow Up'}
            collapsible={true}
            expanded={expandedSections.followUp}
            onToggle={() => toggleSection('followUp')}
          >
            <TextField
              label="Follow Up"
              fullWidth
              value={formData.followUp}
              onChange={handleFollowUpChange}
              disabled={loading}
            />
          </TruCard>

          {/* Top Items */}
          <TruCard 
            title={'Top Items'}
            collapsible={true}
            expanded={expandedSections.topItems}
            onToggle={() => toggleSection('topItems')}
          >
            <AddableList
              label="Add Top Item"
              items={formData.topItems}
              onAdd={addTopLevelItem('topItems')}
              onRemove={removeTopLevelItem('topItems')}
              onUpdate={updateTopLevelItem('topItems')}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={false}
            />
          </TruCard>

          {/* Imprint Remedy */}
          <TruCard 
            title={'Imprint Remedy'}
            collapsible={true}
            expanded={expandedSections.imprintRemedy}
            onToggle={() => toggleSection('imprintRemedy')}
          >
            <TextField
              label="Drops"
              type="text"
              fullWidth
              value={formData.imprintRemedy.drops}
              onChange={handleImprintChange('drops')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Duration (weeks)"
              type="text"
              fullWidth
              value={formData.imprintRemedy.duration}
              onChange={handleImprintChange('duration')}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.imprintRemedy.am}
                    onChange={handleImprintCheckbox('am')}
                    disabled={loading}
                  />
                }
                label="AM"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.imprintRemedy.pm}
                    onChange={handleImprintCheckbox('pm')}
                    disabled={loading}
                  />
                }
                label="PM"
              />
            </Box>
          </TruCard>

          {/* Homeopathics */}
          <TruCard 
            title={'Homeopathics'}
            collapsible={true}
            expanded={expandedSections.homeopathics}
            onToggle={() => toggleSection('homeopathics')}
          >
            <Button
              variant="outlined"
              onClick={addHomeopathic}
              disabled={loading}
              sx={{ mb: 3 }}
            >
              Add Homeopathic
            </Button>

            {formData.homeopathics.map((homeo, index) => (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  p: 3,
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  backgroundColor: '#fafafa'
                }}
              >
                <TextField
                  label="Item"
                  fullWidth
                  value={homeo.item}
                  onChange={(e) => updateHomeopathic(index, 'item', e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Dose"
                  fullWidth
                  value={homeo.dose}
                  onChange={(e) => updateHomeopathic(index, 'dose', e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Duration (weeks)"
                  type="text"
                  fullWidth
                  value={homeo.duration}
                  onChange={(e) => updateHomeopathic(index, 'duration', e.target.value)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={homeo.am}
                        onChange={(e) => updateHomeopathic(index, 'am', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="AM"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={homeo.pm}
                        onChange={(e) => updateHomeopathic(index, 'pm', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="PM"
                  />
                </Box>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => removeHomeopathic(index)}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  Remove Homeopathic
                </Button>
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={addHomeopathic}
              disabled={loading}
              sx={{ mb: 3 }}
            >
              Add Homeopathic
            </Button>
          </TruCard>

          {/* Asea */}
          <TruCard 
            title={'Asea'}
            collapsible={true}
            expanded={expandedSections.asea}
            onToggle={() => toggleSection('asea')}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.asea.summary}
                  onChange={handleAseaCheckbox('summary')}
                  disabled={loading}
                />
              }
              label="Include Summary"
              sx={{ mb: 3 }}
            />

            {/* Liquid */}
            <Box sx={{ mb: 4, p: 2, border: '1px dashed #ddd', borderRadius: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Liquid</div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.asea.liquid.enabled}
                    onChange={handleAseaSubCheckbox('liquid', 'enabled')}
                    disabled={loading}
                  />
                }
                label="Enabled"
                sx={{ mb: 2 }}
              />
              <TextField
                label="First (bolded)"
                fullWidth
                value={formData.asea.liquid.first}
                onChange={handleAseaSubChange('liquid', 'first')}
                disabled={loading}
              />
              <div style={{ marginBottom: 20, marginTop: 10, fontSize: 12 }}>Base Text: oz AM and oz PM for weeks</div>
              <TextField
                label="Second (not bolded)"
                fullWidth
                value={formData.asea.liquid.second}
                onChange={handleAseaSubChange('liquid', 'second')}
                disabled={loading}
              />
              <div style={{ marginBottom: 8, marginTop: 10, fontSize: 12 }}>Base Text: (Start with 2-3 oz/day, and work up over 1 week)</div>
            </Box>

            {/* Gel */}
            <Box sx={{ p: 2, border: '1px dashed #ddd', borderRadius: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Gel</div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.asea.gel.enabled}
                    onChange={handleAseaSubCheckbox('gel', 'enabled')}
                    disabled={loading}
                  />
                }
                label="Enabled"
                sx={{ mb: 2 }}
              />
              <TextField
                label="First (not bolded)"
                fullWidth
                value={formData.asea.gel.first}
                onChange={handleAseaSubChange('gel', 'first')}
                disabled={loading}
              />
              <div style={{ marginBottom: 20, marginTop: 10, fontSize: 12 }}>Base Text: Apply to neck (sides + sides + back), to stomach - in belly button, and any areas with bad rash.</div>
              <TextField
                label="Second (bolded)"
                fullWidth
                value={formData.asea.gel.second}
                onChange={handleAseaSubChange('gel', 'second')}
                disabled={loading}
              />
              <div style={{ marginBottom: 8, marginTop: 10, fontSize: 12 }}>Base Text: Apply Twice Daily</div>
            </Box>
          </TruCard>

          {/* Supplements (Groups) */}
          <TruCard 
            title={'Supplements'}
            collapsible={true}
            expanded={expandedSections.herbs}
            onToggle={() => toggleSection('herbs')}
          >
            <Button
              variant="outlined"
              onClick={addHerbGroup}
              disabled={loading}
              sx={{ mb: 3 }}
            >
              Add Supplement Group
            </Button>

            <Box
              sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2 }}
            >
            {formData.herbs.groups.map((group, gIndex) => {
              const groupTitle = group.heading 
                ? `Supplement Group: ${group.heading}` 
                : `Supplement Group ${gIndex + 1}`;

              return (
                <TruCard 
                  key={gIndex}
                  title={groupTitle}
                  collapsible={true}
                  expanded={expandedGroups[gIndex] ?? false}
                  onToggle={() => toggleGroup(gIndex)}
                >
                  <TextField
                    label="Group Heading"
                    fullWidth
                    value={group.heading}
                    onChange={(e) => updateHerbGroup(gIndex, 'heading', e.target.value)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={group.showHeading}
                        onChange={(e) => updateHerbGroup(gIndex, 'showHeading', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Show Heading"
                    sx={{ mb: 3 }}
                  />

                  <Button
                    variant="outlined"
                    onClick={() => addHerbItem(gIndex)}
                    disabled={loading}
                    sx={{ mb: 2 }}
                  >
                    Add Supplement to Group
                  </Button>

                  {group.items && group.items.map((herb, hIndex) => (
                    <Box
                      key={hIndex}
                      sx={{
                        mb: 3,
                        p: 2,
                        border: '1px dashed #ccc',
                        borderRadius: 1
                      }}
                    >
                      <TextField
                        label="Item"
                        fullWidth
                        value={herb.item}
                        onChange={(e) => updateHerbItem(gIndex, hIndex, 'item', e.target.value)}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Special Note"
                        fullWidth
                        multiline
                        rows={2}
                        value={herb.specialNote}
                        onChange={(e) => updateHerbItem(gIndex, hIndex, 'specialNote', e.target.value)}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Dose"
                        fullWidth
                        value={herb.dose}
                        onChange={(e) => updateHerbItem(gIndex, hIndex, 'dose', e.target.value)}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Duration"
                        fullWidth
                        value={herb.duration}
                        onChange={(e) => updateHerbItem(gIndex, hIndex, 'duration', e.target.value)}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={herb.am}
                              onChange={(e) => updateHerbItem(gIndex, hIndex, 'am', e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label="AM"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={herb.midDay}
                              onChange={(e) => updateHerbItem(gIndex, hIndex, 'midDay', e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label="Mid Day"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={herb.pm}
                              onChange={(e) => updateHerbItem(gIndex, hIndex, 'pm', e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label="PM"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={herb.bedtime}
                              onChange={(e) => updateHerbItem(gIndex, hIndex, 'bedtime', e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label="Bedtime"
                        />
                      </Box>

                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => removeHerbItem(gIndex, hIndex)}
                        disabled={loading}
                        sx={{ mt: 2 }}
                      >
                        Remove Supplement
                      </Button>
                    </Box>
                  ))}

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => removeHerbGroup(gIndex)}
                      disabled={loading}
                    >
                      Remove Group
                    </Button>
                  </Box>
                </TruCard>
              );
            })}
            </Box>

            {/* Special Notes */}
            <Box sx={{ mt: 4 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Special Notes</div>
              <AddableList
                label="Add Special Note"
                items={formData.herbs.specialNotes}
                onAdd={addSpecialNote}
                onRemove={removeSpecialNote}
                onUpdate={updateSpecialNote}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
              />
            </Box>
          </TruCard>

          {/* Additional Recommendations */}
          <TruCard 
            title={'Additional Recommendations'}
            collapsible={true}
            expanded={expandedSections.recommendations}
            onToggle={() => toggleSection('recommendations')}
          >
            {/* Therapies */}
            <Box sx={{ mb: 4 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Beneficial Therapies</div>
              <AddableList
                label="Add Therapy"
                items={formData.recommendations.therapies}
                onAdd={addNestedItem('recommendations', 'therapies')}
                onRemove={removeNestedItem('recommendations', 'therapies')}
                onUpdate={updateNestedItem('recommendations', 'therapies')}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={false}
              />
            </Box>

            {/* Recommendations */}
            <Box>
              <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Recommendations</div>
              <AddableList
                label="Add Recommendation"
                items={formData.recommendations.recommendations}
                onAdd={addNestedItem('recommendations', 'recommendations')}
                onRemove={removeNestedItem('recommendations', 'recommendations')}
                onUpdate={updateNestedItem('recommendations', 'recommendations')}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
              />
            </Box>
          </TruCard>

          {/* Key Notes From Scan */}
          <TruCard 
            title={'Key Notes From Scan'}
            collapsible={true}
            expanded={expandedSections.keyNotes}
            onToggle={() => toggleSection('keyNotes')}
          >
            {/* Priorities */}
            <Box sx={{ mb: 4 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Priorities</div>
              <AddableList
                label="Add Priority"
                items={formData.keyNotes.priorities}
                onAdd={addNestedItem('keyNotes', 'priorities')}
                onRemove={removeNestedItem('keyNotes', 'priorities')}
                onUpdate={updateNestedItem('keyNotes', 'priorities')}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={false}
              />
            </Box>

            {/* Notes */}
            <Box>
              <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Additional Notes</div>
              <AddableList
                label="Add Note"
                items={formData.keyNotes.notes}
                onAdd={addNestedItem('keyNotes', 'notes')}
                onRemove={removeNestedItem('keyNotes', 'notes')}
                onUpdate={updateNestedItem('keyNotes', 'notes')}
                allowEdit={true}
                disabled={loading}
                isObject={false}
                multiline={true}
              />
            </Box>
          </TruCard>

          {/* Items You Order */}
          <TruCard 
            title={'Items You Order'}
            collapsible={true}
            expanded={expandedSections.itemsToOrder}
            onToggle={() => toggleSection('itemsToOrder')}
          >
            <AddableList
              label="Add Item"
              items={formData.itemsToOrder}
              onAdd={addTopLevelItem('itemsToOrder')}
              onRemove={removeTopLevelItem('itemsToOrder')}
              onUpdate={updateTopLevelItem('itemsToOrder')}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={false}
            />
          </TruCard>

          {/* I Ship To You */}
          <TruCard 
            title={'I Ship To You'}
            collapsible={true}
            expanded={expandedSections.itemsToShip}
            onToggle={() => toggleSection('itemsToShip')}
          >
            <AddableList
              label="Add Item"
              items={formData.itemsToShip}
              onAdd={addTopLevelItem('itemsToShip')}
              onRemove={removeTopLevelItem('itemsToShip')}
              onUpdate={updateTopLevelItem('itemsToShip')}
              allowEdit={true}
              disabled={loading}
              isObject={false}
              multiline={false}
            />
          </TruCard>

          {/* Submit / Cancel */}
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate(isOneOff ? `/admin/reports/session/${reportId}` : `/clients/${clientId}/scans/${scanId}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ flex: 3 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </div>
  );
}